<!--
  Flexi › Exposure Register › one assessment.
  Introduction → Departments → Assessment → Summary, then Complete.
  Everything saves automatically. Read-only once completed, and for anyone who
  is not an author.
-->
<section class="fvr-card fer-wizard" [attr.aria-busy]="loading || refreshing > 0">
  <header class="fer-wizard__head">
    <a class="fvr-back" href="javascript:void(0)" (click)="backToOverview()">
      <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.TAB' | translate }}
    </a>
    @if (assessment; as a) {
      <div class="fer-wizard__title-row">
        <div class="fer-wizard__title">
          <h2 class="fvr-title">{{ a.Title }}</h2>
          @if (a.Status === Status.Completed) {
            <span class="fvr-pill fvr-pill--done">{{ 'FLEXI_EXPOSURE.STATUS.COMPLETED' | translate }}</span>
          } @else {
            <span class="fvr-pill fvr-pill--progress">{{ 'FLEXI_EXPOSURE.STATUS.IN_PROGRESS' | translate }}</span>
          }
        </div>
        <p class="fer-wizard__count">
          {{ 'FLEXI_EXPOSURE.FACTORS_ASSESSED_OF' | translate: { count: progress.assessed, total: progress.total } }}
        </p>
      </div>
      <p class="fvr-subtitle">
        {{ a.TemplateName || '—' }}
        @if (a.ResponsibleUserName) {
          · {{ 'FLEXI_EXPOSURE.RESPONSIBLE_NAMED' | translate: { name: a.ResponsibleUserName } }}
        }
        @if (a.CompletedOn) {
          · {{ 'FLEXI_EXPOSURE.COMPLETED_ON' | translate: { date: (a.CompletedOn | date: 'dd-MM-yyyy') } }}
        } @else {
          · {{ 'FLEXI_EXPOSURE.STARTED_ON' | translate: { date: (a.CreatedOn | date: 'dd-MM-yyyy') } }}
        }
      </p>
    }
  </header>

  @if (loading && !assessment) {
    <flexi-safety-round-skeleton layout="form" />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_EXPOSURE.ASSESSMENT_LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_EXPOSURE.RETRY' | translate }}</button>
  }

  @if (downloading || refreshing > 0) {
    <p class="fvr-action-status" role="status">
      <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
      {{ (downloading ? 'FLEXI_EXPOSURE.FEEDBACK.REPORT' : 'FLEXI_EXPOSURE.FEEDBACK.UPDATING') | translate }}
    </p>
  }

  @if (assessment; as a) {
    @if (isCompleted) {
      <p class="fas-note">
        <i class="pi pi-lock" aria-hidden="true"></i>
        <span>{{ 'FLEXI_EXPOSURE.READ_ONLY_COMPLETED' | translate }}</span>
      </p>
    } @else if (!canAuthor) {
      <p class="fas-note">
        <i class="pi pi-eye" aria-hidden="true"></i>
        <span>{{ 'FLEXI_EXPOSURE.READ_ONLY_VIEWER' | translate }}</span>
      </p>
    }

    <!-- ═══════════ Step bar ═══════════ -->
    <ol class="fer-steps" [attr.aria-label]="'FLEXI_EXPOSURE.STEPS_LABEL' | translate">
      @for (s of steps; track s) {
        <li class="fer-steps__item" [class.fer-steps__item--reached]="s <= step" [attr.aria-current]="s === step ? 'step' : null">
          <span class="fer-steps__bar" aria-hidden="true"></span>
          <button type="button" class="fer-steps__button" [class.fer-steps__button--current]="s === step" (click)="selectStep(s)">
            {{ s }} · {{ stepKey(s) | translate }}
          </button>
        </li>
      }
    </ol>

    <!-- ═══════════ Step 1: introduction ═══════════ -->
    @if (step === Step.Introduction) {
      <div class="fer-step">
        @if (!readOnly) {
          <section class="fer-block" aria-labelledby="fer-details-heading">
            <h3 class="fvr-h3" id="fer-details-heading">{{ 'FLEXI_EXPOSURE.DETAILS.TITLE' | translate }}</h3>
            <div class="fer-block__grid">
              <div class="fvr-field">
                <label for="fer-title">{{ 'FLEXI_EXPOSURE.DETAILS.NAME' | translate }} <span class="fvr-required">*</span></label>
                <input
                  pInputText
                  id="fer-title"
                  type="text"
                  maxlength="300"
                  [(ngModel)]="title"
                  (ngModelChange)="titleInvalid = false"
                  [attr.aria-invalid]="titleInvalid"
                  aria-describedby="fer-title-error"
                />
                @if (titleInvalid) {
                  <span class="fer-field-error" id="fer-title-error" role="alert">{{ 'FLEXI_EXPOSURE.DETAILS.NAME_REQUIRED' | translate }}</span>
                }
              </div>
              <div class="fvr-field">
                <label for="fer-responsible">{{ 'FLEXI_EXPOSURE.RESPONSIBLE' | translate }}</label>
                <p-select
                  inputId="fer-responsible"
                  [options]="users"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="responsibleUserId"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body"
                  [placeholder]="'FLEXI_EXPOSURE.SELECT' | translate"
                  styleClass="fer-select"
                ></p-select>
              </div>
            </div>
            <span class="fvr-hint">{{ 'FLEXI_EXPOSURE.DETAILS.HINT' | translate }}</span>
          </section>
        }

        <section class="fer-block" aria-labelledby="fer-intro-heading">
          <h3 class="fvr-h3" id="fer-intro-heading">{{ 'FLEXI_EXPOSURE.INTRO.TITLE' | translate }}</h3>
          <p class="fer-prose">{{ 'FLEXI_EXPOSURE.INTRO.P1' | translate }}</p>
          <p class="fer-prose">{{ 'FLEXI_EXPOSURE.INTRO.P2' | translate }}</p>
          <p class="fer-prose">{{ 'FLEXI_EXPOSURE.INTRO.P3' | translate }}</p>
        </section>

        <section class="fer-block" aria-labelledby="fer-company-heading">
          <div class="fer-block__head">
            <h3 class="fvr-h3" id="fer-company-heading">{{ 'FLEXI_EXPOSURE.ABOUT.TITLE' | translate }}</h3>
            <span class="fas-muted">
              {{ (isCompleted ? 'FLEXI_EXPOSURE.ABOUT.SNAPSHOT' : 'FLEXI_EXPOSURE.ABOUT.FROM_OVERVIEW') | translate }}
              @if (!readOnly) {
                · <button type="button" class="fvr-link" (click)="editBasisTexts()">{{ 'FLEXI_EXPOSURE.ABOUT.EDIT' | translate }}</button>
              }
            </span>
          </div>
          <div class="fer-basis">
            <h4 class="fer-basis__label">{{ 'FLEXI_EXPOSURE.ABOUT.INDUSTRY' | translate }}</h4>
            <p class="fer-prose">{{ a.IndustryExposures || ('FLEXI_EXPOSURE.ABOUT.NOT_FILLED' | translate) }}</p>
            <span class="fas-muted">{{ 'FLEXI_EXPOSURE.ABOUT.SOURCE_NOA' | translate }}</span>
          </div>
          <div class="fer-basis">
            <h4 class="fer-basis__label">{{ 'FLEXI_EXPOSURE.ABOUT.PRODUCTS_SHORT' | translate }}</h4>
            <p class="fer-prose">{{ a.ProductsAndServices || ('FLEXI_EXPOSURE.ABOUT.NOT_FILLED' | translate) }}</p>
          </div>
          <div class="fer-basis">
            <h4 class="fer-basis__label">{{ 'FLEXI_EXPOSURE.ABOUT.LOCAL' | translate }}</h4>
            <p class="fer-prose">{{ a.LocalClarifications || ('FLEXI_EXPOSURE.ABOUT.NOT_FILLED' | translate) }}</p>
          </div>
        </section>
      </div>
    }

    <!-- ═══════════ Step 2: departments ═══════════ -->
    @if (step === Step.Departments) {
      <section class="fer-step fer-block" aria-labelledby="fer-departments-heading">
        <div class="fer-block__head">
          <div>
            <h3 class="fvr-h3" id="fer-departments-heading">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.TITLE' | translate }}</h3>
            <p class="fas-muted fer-block__intro">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.INTRO' | translate }}</p>
          </div>
          <span class="fer-wizard__count" aria-live="polite">
            {{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.SELECTED' | translate: { count: included.length, total: departments.length } }}
          </span>
        </div>

        @if (!departments.length) {
          <div class="fvr-warning" role="note">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.NONE' | translate }}</span>
          </div>
        }

        <div class="fer-departments">
          @for (department of departments; track department.DepartmentId) {
            <div class="fer-department" [class.fer-department--excluded]="!department.IsIncluded">
              <div class="fer-department__row">
                <input
                  type="checkbox"
                  [id]="'fer-dept-' + department.DepartmentId"
                  [checked]="department.IsIncluded"
                  [disabled]="readOnly"
                  (change)="toggleDepartment(department)"
                />
                <label class="fer-department__name" [for]="'fer-dept-' + department.DepartmentId">{{ department.Name }}</label>
                <span class="fas-muted">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.EMPLOYEES' | translate: { count: department.EmployeeCount } }}</span>
              </div>
              @if (department.IsIncluded) {
                <div class="fvr-field fer-department__text">
                  <label [for]="'fer-dept-text-' + department.DepartmentId">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.EXPOSURE' | translate }}</label>
                  <textarea
                    pTextarea
                    rows="3"
                    maxlength="4000"
                    [id]="'fer-dept-text-' + department.DepartmentId"
                    [attr.aria-describedby]="'fer-dept-help-' + department.DepartmentId"
                    [(ngModel)]="department.ExposureDescription"
                    (ngModelChange)="departmentTextChanged()"
                    [readonly]="readOnly"
                    [placeholder]="'FLEXI_EXPOSURE.DEPARTMENTS_STEP.EXPOSURE_PLACEHOLDER' | translate"
                  ></textarea>
                  @if (!department.ExposureDescription && !readOnly) {
                    <span class="fvr-hint" [id]="'fer-dept-help-' + department.DepartmentId">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.EXPOSURE_HELP' | translate }}</span>
                  }
                </div>
              }
            </div>
          }
        </div>

        <p class="fas-muted">{{ 'FLEXI_EXPOSURE.DEPARTMENTS_STEP.MISSING' | translate }}</p>
      </section>
    }

    <!-- ═══════════ Step 3: assessment ═══════════ -->
    @if (step === Step.Assessment) {
      <div class="fer-step">
        <p class="fas-muted fer-block__intro">{{ 'FLEXI_EXPOSURE.ASSESS.INTRO' | translate }}</p>
        @if (!included.length && !readOnly) {
          <div class="fvr-warning" role="note">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>
              {{ 'FLEXI_EXPOSURE.ASSESS.NO_DEPARTMENTS' | translate }}
              <button type="button" class="fvr-link" (click)="selectStep(Step.Departments)">{{ 'FLEXI_EXPOSURE.STEP.2' | translate }}</button>
            </span>
          </div>
        }

        <div class="fer-sections">
          @for (section of sections; track section.Name) {
            @let stats = sectionProgress.get(section.Name);
            <section class="fer-section">
              <h3 class="fer-section__heading">
                <button
                  type="button"
                  class="fer-section__toggle"
                  [attr.aria-expanded]="openSection === section.Name"
                  [attr.aria-controls]="'fer-section-' + section.Position"
                  (click)="toggleSection(section)"
                >
                  <i class="pi" [ngClass]="openSection === section.Name ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
                  <span class="fer-section__name">{{ section.Name }}</span>
                  @if (stats?.relevant) {
                    <span class="fer-status fer-status--relevant">
                      <span class="fer-marker fer-marker--relevant" aria-hidden="true"></span>
                      {{ 'FLEXI_EXPOSURE.ASSESS.N_RELEVANT' | translate: { count: stats?.relevant } }}
                    </span>
                  }
                  <span class="fas-muted">{{ 'FLEXI_EXPOSURE.ASSESS.N_OF_M_ASSESSED' | translate: { count: stats?.assessed ?? 0, total: section.Factors.length } }}</span>
                </button>
              </h3>

              @if (openSection === section.Name) {
                <div class="fer-section__body" [id]="'fer-section-' + section.Position">
                  @for (factor of section.Factors; track factor.Id) {
                    <div class="fer-factor" [class.fer-factor--open]="openFactorId === factor.Id">
                      <h4 class="fer-factor__heading">
                        <button
                          type="button"
                          class="fer-factor__toggle"
                          [attr.aria-expanded]="openFactorId === factor.Id"
                          [attr.aria-controls]="'fer-factor-' + factor.Id"
                          (click)="toggleFactor(factor)"
                        >
                          <span class="fer-factor__title">{{ factor.Title }}</span>
                          @if (factorDepartmentText(factor); as names) {
                            <span class="fas-muted fer-factor__departments">{{ names }}</span>
                          }
                          <span class="fer-status fer-factor__status" [ngClass]="'fer-status--' + marker(factor.HealthCheck)">
                            <span class="fer-marker" [ngClass]="'fer-marker--' + marker(factor.HealthCheck)" aria-hidden="true"></span>
                            {{ labelKey(factor.HealthCheck) | translate }}
                          </span>
                        </button>
                      </h4>

                      @if (openFactorId === factor.Id) {
                        <div class="fer-factor__body" [id]="'fer-factor-' + factor.Id">
                          <div class="fer-factor__tools">
                            @if (guidance.get(factor.Id)?.length) {
                              <button type="button" class="fvr-tool" [attr.aria-expanded]="guideOpen.has(factor.Id)" (click)="toggleGuide(factor)">
                                <i class="pi pi-info-circle" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.ASSESS.GUIDANCE' | translate }}
                              </button>
                            }
                            @if (factor.RegulatoryReferences.length) {
                              <button type="button" class="fvr-tool" [attr.aria-expanded]="refsOpen.has(factor.Id)" (click)="toggleRefs(factor)">
                                <i class="pi pi-book" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.ASSESS.LEGAL' | translate }}
                              </button>
                            }
                          </div>

                          @if (guideOpen.has(factor.Id)) {
                            <div class="fer-panel">
                              <span class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.ASSESS.GUIDANCE' | translate }}</span>
                              @for (paragraph of guidance.get(factor.Id); track $index) {
                                <p>{{ paragraph }}</p>
                              }
                            </div>
                          }

                          @if (refsOpen.has(factor.Id)) {
                            <div class="fer-panel">
                              <span class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.ASSESS.LEGAL' | translate }}</span>
                              @for (ref of factor.RegulatoryReferences; track $index) {
                                @if (ref.Url) {
                                  <a class="fvr-link" [href]="ref.Url" target="_blank" rel="noopener noreferrer">
                                    {{ ref.Title }}<span class="fas-sr-only"> ({{ 'FLEXI_EXPOSURE.NEW_WINDOW' | translate }})</span>
                                    <i class="pi pi-external-link" aria-hidden="true"></i>
                                  </a>
                                } @else {
                                  <span>{{ ref.Title }}</span>
                                }
                              }
                            </div>
                          }

                          <fieldset class="fer-fieldset">
                            <legend class="fer-label">{{ 'FLEXI_EXPOSURE.ASSESS.HEALTH_CHECK' | translate }}</legend>
                            <div class="fer-segmented">
                              @for (option of healthChecks; track option) {
                                <button
                                  type="button"
                                  [attr.aria-pressed]="factor.HealthCheck === option"
                                  [disabled]="readOnly"
                                  (click)="setHealthCheck(factor, option)"
                                >
                                  <span class="fer-marker" [ngClass]="'fer-marker--' + marker(option)" aria-hidden="true"></span>
                                  {{ labelKey(option) | translate }}
                                </button>
                              }
                            </div>
                          </fieldset>

                          <div class="fvr-field">
                            <label [for]="'fer-rationale-' + factor.Id">{{ 'FLEXI_EXPOSURE.ASSESS.RATIONALE' | translate }}</label>
                            <textarea
                              pTextarea
                              rows="3"
                              maxlength="4000"
                              [id]="'fer-rationale-' + factor.Id"
                              [(ngModel)]="factor.Rationale"
                              (ngModelChange)="rationaleChanged(factor)"
                              [readonly]="readOnly"
                              [placeholder]="'FLEXI_EXPOSURE.ASSESS.RATIONALE_PLACEHOLDER' | translate"
                            ></textarea>
                          </div>

                          <div class="fer-factor__departments-field">
                            <span class="fer-label" [id]="'fer-applies-' + factor.Id">{{ 'FLEXI_EXPOSURE.ASSESS.APPLIES_TO' | translate }}</span>
                            <div class="fer-chips" role="group" [attr.aria-labelledby]="'fer-applies-' + factor.Id">
                              @for (department of included; track department.DepartmentId) {
                                <button
                                  type="button"
                                  class="fer-chip"
                                  [attr.aria-pressed]="factor.DepartmentIds.includes(department.DepartmentId)"
                                  [disabled]="readOnly"
                                  (click)="toggleFactorDepartment(factor, department.DepartmentId)"
                                >
                                  @if (factor.DepartmentIds.includes(department.DepartmentId)) {
                                    <i class="pi pi-check" aria-hidden="true"></i>
                                  }
                                  {{ department.Name }}
                                </button>
                              } @empty {
                                <span class="fas-muted">{{ 'FLEXI_EXPOSURE.ASSESS.NO_INCLUDED' | translate }}</span>
                              }
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </section>
          } @empty {
            <div class="fvr-empty">
              <i class="pi pi-inbox" aria-hidden="true"></i>
              <p>{{ 'FLEXI_EXPOSURE.ASSESS.NO_FACTORS' | translate }}</p>
            </div>
          }
        </div>
      </div>
    }

    <!-- ═══════════ Step 4: summary ═══════════ -->
    @if (step === Step.Summary) {
      <div class="fer-step">
        <div class="fer-kpis" role="group" [attr.aria-label]="'FLEXI_EXPOSURE.SUMMARY.OUTCOME' | translate">
          <p class="fer-kpi">
            <span class="fer-kpi__label"><span class="fer-marker fer-marker--relevant" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.KPI.RELEVANT' | translate }}</span>
            <span class="fer-kpi__value">{{ progress.relevant }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
          </p>
          <p class="fer-kpi">
            <span class="fer-kpi__label"><span class="fer-marker fer-marker--unclear" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.HEALTH_CHECK.UNCLEAR' | translate }}</span>
            <span class="fer-kpi__value">{{ progress.unclear }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
          </p>
          <p class="fer-kpi">
            <span class="fer-kpi__label"><span class="fer-marker fer-marker--not-relevant" aria-hidden="true"></span>{{ 'FLEXI_EXPOSURE.HEALTH_CHECK.NOT_RELEVANT' | translate }}</span>
            <span class="fer-kpi__value">{{ progress.notRelevant }} <small>{{ 'FLEXI_EXPOSURE.KPI.FACTORS' | translate }}</small></span>
          </p>
          <p class="fer-kpi">
            <span class="fer-kpi__label">{{ 'FLEXI_EXPOSURE.KPI.DEPARTMENTS_RELEVANT' | translate }}</span>
            <span class="fer-kpi__value">{{ relevantDepartmentCount }} <small>{{ 'FLEXI_EXPOSURE.KPI.OF_TOTAL' | translate: { total: included.length } }}</small></span>
          </p>
        </div>

        <section class="fer-summary-table" aria-labelledby="fer-summary-heading">
          <div class="fer-summary-table__toolbar">
            <h3 class="fvr-h3" id="fer-summary-heading">{{ 'FLEXI_EXPOSURE.SUMMARY.TITLE' | translate }}</h3>
            <div class="fer-summary-table__filters">
              <div class="fvr-field fer-summary-table__department">
                <label for="fer-summary-department">{{ 'FLEXI_EXPOSURE.SUMMARY.DEPARTMENT' | translate }}</label>
                <select id="fer-summary-department" class="fer-native-select" [ngModel]="summaryDepartmentId ?? ''" (ngModelChange)="summaryDepartmentChanged($event)">
                  <option value="">{{ 'FLEXI_EXPOSURE.SUMMARY.ALL_DEPARTMENTS' | translate }}</option>
                  @for (department of included; track department.DepartmentId) {
                    <option [value]="department.DepartmentId">{{ department.Name }}</option>
                  }
                </select>
              </div>
              <div class="fer-segmented" role="group" [attr.aria-label]="'FLEXI_EXPOSURE.SUMMARY.FILTER' | translate">
                @for (option of statusFilters; track option.labelKey) {
                  <button type="button" [attr.aria-pressed]="summaryStatus === option.value" (click)="pickStatus(option.value)">
                    {{ option.labelKey | translate }}
                  </button>
                }
              </div>
            </div>
          </div>

          <div class="fvr-table-card fer-table-wrap">
            <table class="fer-table">
              <thead>
                <tr>
                  <th scope="col" class="fer-table__factor">{{ 'FLEXI_EXPOSURE.COLUMN.FACTOR' | translate }}</th>
                  <th scope="col" class="fer-table__status">{{ 'FLEXI_EXPOSURE.ASSESS.HEALTH_CHECK' | translate }}</th>
                  <th scope="col" class="fer-table__departments">{{ 'FLEXI_EXPOSURE.DEPARTMENTS' | translate }}</th>
                  <th scope="col">{{ 'FLEXI_EXPOSURE.ASSESS.RATIONALE' | translate }}</th>
                </tr>
              </thead>
              @for (group of groups; track group.name) {
                <tbody>
                  <tr>
                    <th scope="colgroup" colspan="4" class="fer-table__group">{{ group.name }}</th>
                  </tr>
                  @for (factor of group.factors; track factor.Id) {
                    <tr>
                      <th scope="row" class="fer-table__factor">{{ factor.Title }}</th>
                      <td>
                        <span class="fer-status" [ngClass]="'fer-status--' + marker(factor.HealthCheck)">
                          <span class="fer-marker" [ngClass]="'fer-marker--' + marker(factor.HealthCheck)" aria-hidden="true"></span>
                          {{ labelKey(factor.HealthCheck) | translate }}
                        </span>
                      </td>
                      <td>{{ factorDepartmentText(factor) || '–' }}</td>
                      <td class="fer-table__rationale">{{ factor.Rationale || '–' }}</td>
                    </tr>
                  }
                </tbody>
              }
            </table>
            @if (!groups.length) {
              <p class="fer-table__empty">{{ 'FLEXI_EXPOSURE.SUMMARY.EMPTY' | translate }}</p>
            }
          </div>
        </section>

        @if (completeBlockedKey; as blocked) {
          <div class="fvr-warning" role="note">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>
              {{ blocked | translate: { count: completion.unassessed } }}
              @if (completion.includedDepartments === 0) {
                <button type="button" class="fvr-link" (click)="selectStep(Step.Departments)">{{ 'FLEXI_EXPOSURE.STEP.2' | translate }}</button>
              } @else if (completion.unassessed > 0) {
                <button type="button" class="fvr-link" (click)="goToFirstUnassessed()">{{ 'FLEXI_EXPOSURE.SUMMARY.GO_FIRST_UNASSESSED' | translate }}</button>
              }
            </span>
          </div>
        }
      </div>
    }

    <!-- ═══════════ Footer: the way on, and where the saves stand ═══════════ -->
    <div class="fer-footer">
      @if (step === Step.Introduction) {
        <button type="button" class="fas-btn fas-btn--ghost" (click)="backToOverview()">
          {{ (readOnly ? 'FLEXI_EXPOSURE.BACK' : 'CANCEL') | translate }}
        </button>
      } @else {
        <button type="button" class="fas-btn fas-btn--ghost" (click)="previousStep()">
          <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.PREVIOUS' | translate }}
        </button>
      }
      <span class="fer-footer__spacer"></span>
      @if (!readOnly) {
        <span class="fer-sync" role="status">
          @switch (saveState) {
            @case ('saving') {
              <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.SAVING' | translate }}
            }
            @case ('saved') {
              <i class="pi pi-check" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.SAVED_AT' | translate: { time: (savedAt | date: 'HH:mm') } }}
            }
            @case ('failed') {
              <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.SAVE_FAILED' | translate }}
            }
          }
        </span>
      }
      @if (step === Step.Summary) {
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="downloading" (click)="downloadReport()">
          <i class="pi" [ngClass]="downloading ? 'pi-spin pi-spinner' : 'pi-file-pdf'" aria-hidden="true"></i>
          {{ (isCompleted ? 'FLEXI_EXPOSURE.OPEN_REPORT' : 'FLEXI_EXPOSURE.SUMMARY.PREVIEW_REPORT') | translate }}
        </button>
        @if (!readOnly) {
          <button type="button" class="fas-btn fas-btn--primary" [disabled]="!canSubmitCompletion" (click)="openComplete()">
            {{ 'FLEXI_EXPOSURE.SUMMARY.COMPLETE' | translate }}
          </button>
        }
      } @else {
        <button type="button" class="fas-btn fas-btn--primary" (click)="nextStep()">
          {{ 'FLEXI_EXPOSURE.NEXT' | translate }}<i class="pi pi-angle-right" aria-hidden="true"></i>
        </button>
      }
    </div>
  }
</section>

@if (completeOpen && assessment) {
  <flexi-exposure-complete-dialog
    [businessId]="businessId"
    [assessmentId]="assessment.Id"
    [assessmentTitle]="assessment.Title"
    [relevantCount]="progress.relevant"
    [relevantDepartmentCount]="relevantDepartmentCount"
    [actionPointsUrl]="actionPointsUrl"
    (completed)="completed()"
    (openReport)="downloadReport()"
    (backToOverview)="backToOverview()"
    (closed)="completeClosed()"
  ></flexi-exposure-complete-dialog>
}
